import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import api from '../../api/client';
import { queryKeys } from './queryKeys';
import useProtectedQueryEnabled from './useProtectedQueryEnabled';

const HISTORY_PAGE_SIZE = 20;
const LEGACY_HISTORY_LIMIT = 30;

export async function fetchHistoryPage(
  { type = 'all', period = 'all', cursor = null, limit = HISTORY_PAGE_SIZE },
  signal,
) {
  const params = { limit };
  if (type !== 'all') params.type = type;
  if (period !== 'all') params.period = period;
  if (cursor) params.cursor = cursor;

  const res = await api.get('/history', { params, signal });
  return {
    items: res.data?.items ?? [],
    nextCursor: res.data?.nextCursor ?? null,
    hasMore: Boolean(res.data?.hasMore),
  };
}

function getDateRangeByPeriod(period) {
  if (period === 'ALL') return { dateFrom: null, dateTo: null };

  const now = new Date();
  const to = new Date(now);
  const from = new Date(now);
  if (period === '7D') from.setDate(from.getDate() - 7);
  else if (period === '30D') from.setDate(from.getDate() - 30);
  else if (period === '365D') from.setDate(from.getDate() - 365);

  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}

export async function fetchLegacyUserHistory(
  { type = 'ALL', period = 'ALL', offset = 0, limit = LEGACY_HISTORY_LIMIT },
  signal,
) {
  const params = { limit, offset };
  if (type !== 'ALL') params.type = type;
  const range = getDateRangeByPeriod(period);
  if (range.dateFrom) params.dateFrom = range.dateFrom;
  if (range.dateTo) params.dateTo = range.dateTo;

  const res = await api.get('/user/history', { params, signal });
  const events = res.data?.events || [];
  return {
    events,
    summary: res.data?.summary || null,
    offset,
    nextOffset: offset + events.length,
    hasMore: Boolean(res.data?.pagination?.hasMore),
  };
}

export function useHistoryInfiniteQuery({
  type = 'all',
  period = 'all',
  enabled = true,
} = {}) {
  const protectedEnabled = useProtectedQueryEnabled(enabled);
  return useInfiniteQuery({
    queryKey: queryKeys.history({ type, period }),
    queryFn: ({ pageParam = null, signal }) =>
      fetchHistoryPage({ type, period, cursor: pageParam }, signal),
    initialPageParam: null,
    enabled: protectedEnabled,
    getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.nextCursor : undefined),
  });
}

export function useLegacyUserHistoryInfiniteQuery({ type = 'ALL', period = 'ALL' } = {}) {
  const protectedEnabled = useProtectedQueryEnabled();
  return useInfiniteQuery({
    queryKey: queryKeys.legacyUserHistory({ type, period }),
    queryFn: ({ pageParam = 0, signal }) =>
      fetchLegacyUserHistory({ type, period, offset: pageParam }, signal),
    initialPageParam: 0,
    enabled: protectedEnabled,
    getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.nextOffset : undefined),
  });
}

export function useLegacyUserHistoryQuery({ type = 'ALL', period = 'ALL' } = {}) {
  const protectedEnabled = useProtectedQueryEnabled();
  return useQuery({
    queryKey: [...queryKeys.legacyUserHistory({ type, period }), 'first-page'],
    queryFn: ({ signal }) => fetchLegacyUserHistory({ type, period, offset: 0 }, signal),
    enabled: protectedEnabled,
  });
}
